import { DeleteIcon, LinkIcon, PlusIcon } from 'components/icons/icons'
import { useCallback, useMemo, useRef, useState } from 'react'
import styles from './linkCell.module.scss'
import { Input } from 'components/Form'
import { getAbsoluteUrl } from '../LinkCell/helper'
import { Cover } from 'components/Cover'
import { getCssVariableValue } from 'utils/cssVariablesUtils'
import { PortalToDropdown } from "components/Portal/PortalToDropdown"

const isValidLink = (link: string) => {
	const regex = /^(https?:\/\/|www\.)[\w-]+(\.[\w-]+)+([/?#].*)?$/i
	return regex.test(link);
}

type Props = {
	value: string
	onChange(value: string): void
	onBlur(): void
}

export const LinkEditableCell = ({ value, onChange, onBlur }: Props) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [newLink, setNewLink] = useState('');
	const [showLinkInput, setShowLinkInput] = useState(false);
	const [errorStyle, setErrorStyle] = useState<React.CSSProperties>({});

	const openCellDropdownCallback = useCallback(
		() => setIsOpen(true),
		[]
	)

	const closeCellDropdownCallback = useCallback(
		() => setIsOpen(false),
		[]
	)

	const handleOpenLinkCallback = useCallback(
		(link: string) => {
			const url = getAbsoluteUrl(link);
			window.open(url, '_blank');
			closeCellDropdownCallback();
		},
		[closeCellDropdownCallback]
	)

	const handleDeleteCallback = useCallback(
		(uri: string) => {
			const updatedLinks = value.split(';').filter(link => link !== uri).join(';');
			onChange(updatedLinks);
		},
		[value, onChange]
	)

	const handleOnChangeCallback = useCallback(
		() => {
			if (isValidLink(newLink)) {
				const updatedLinks = value ? `${value};${newLink}` : newLink;
				onChange(updatedLinks);
				setNewLink('');
				setShowLinkInput(false);
			} else {
				setErrorStyle({ borderColor: getCssVariableValue('--error-color') });
			}
		},
		[value, newLink, onChange]
	)

	const handleOnBlurCallback = useCallback(
		() => {
			onBlur();
			handleOnChangeCallback();
		},
		[onBlur, handleOnChangeCallback]
	)

	const onKeyDownCallback = useCallback(
		(eventKey: string) => {
			if (eventKey === 'Enter') {
				handleOnChangeCallback();
			}
		},
		[handleOnChangeCallback]
	)

	const linkCount = useMemo(
		() => (value || '').split(';').filter(x => !!x).length,
		[value]
	)

	const dropdownContentMemo = useMemo(
		() => (
			<div className={styles.dropdown_content}>
				{(value || '').split(';').filter(x => !!x).map((link, i) => (
					<div className={styles.link_item} key={i}>
						<div
							className={styles.link}
							onClick={() => handleOpenLinkCallback(link)}
							title={link}
						>
							{link}
						</div>
						<div className={styles.delete_icon}>
							<DeleteIcon width={10} height={10} fill='currentColor' onClick={() => handleDeleteCallback(link)} />
						</div>
					</div>
				))}
				{!showLinkInput ?
					<div className={styles.add_icon} onClick={() => setShowLinkInput(true)}>
						<PlusIcon width={10} height={10} fill='currentColor' />
					</div>
					:
					<Input
						value={newLink}
						onChange={(newValue: string) => setNewLink(newValue)}
						onBlur={handleOnBlurCallback}
						onKeyDown={onKeyDownCallback}
						style={errorStyle}
						hideMaxLength
						size='small'
						focus
					/>
				}
			</div>
		),
		[value, showLinkInput, newLink, errorStyle, handleOnBlurCallback, handleOpenLinkCallback, handleDeleteCallback, onKeyDownCallback]
	)

	return (
		<div ref={containerRef} className={styles.container}>
			{isOpen && <Cover onClick={closeCellDropdownCallback} transparent />}
			<div onClick={openCellDropdownCallback}>
				<LinkIcon width={12} height={12} fill='currentColor' />
				{linkCount > 0 && <span className={styles.link_badge}>{linkCount}</span>}
			</div>
			{isOpen &&
				<PortalToDropdown containerRef={containerRef}>
					{dropdownContentMemo}
				</PortalToDropdown>
			}
		</div>
	)
}
